import { useSelector } from "react-redux";
import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { PaymentsOverviewContainer, HeadingContainer, Title, Price, ItemsList, ItemTitle, ItemDivider } from "./styles";

import { monetaryConfig } from "state/config-slice";
import Offer from "types/Offer";

import { calculateOffersTotal } from "utils/calculateOffersTotal";

import PaymentItem from "containers/PaymentsOverview/PaymentItem";
import generateCurrencyValue from "utils/generateCurrencyValue";
import FeatureWrapper from "components/FeatureToggle/FeatureWrapper";

interface IPaymentsOverview {
  title: string;
  offers: Offer[];
  isFromCheckout?: boolean;
}

const PaymentsOverview = ({ title, offers, isFromCheckout = false }: IPaymentsOverview) => {
  const { t } = useTranslation();
  const monetarySettings = useSelector(monetaryConfig);

  const netflixOffer = useMemo(
    () =>
      offers.find(({ marketplaceInfo, partner }) => {
        const isStartingPrice = marketplaceInfo.isStartingPrice ?? marketplaceInfo.agency;

        return partner.toLowerCase() === "netflix" && isStartingPrice;
      }),
    [offers],
  );

  const offersWithoutNetflix = useMemo(
    () =>
      offers.filter(({ marketplaceInfo, partner }) => {
        const isStartingPrice = marketplaceInfo.isStartingPrice ?? marketplaceInfo.agency;

        return partner.toLowerCase() !== "netflix" || (partner.toLowerCase() === "netflix" && !isStartingPrice);
      }),
    [offers],
  );

  const offersWithPriceText = offers.find((offer) => offer.marketplaceInfo["priceText"])?.marketplaceInfo["priceText"];

  const total = useMemo(() => calculateOffersTotal(offersWithoutNetflix), [offersWithoutNetflix]);

  const payment = useMemo(() => generateCurrencyValue(total, monetarySettings), [total, monetarySettings]);

  return (
    <PaymentsOverviewContainer data-testid="payments-overview" $isFromCheckout={isFromCheckout}>
      <FeatureWrapper name="Show paymnetoverview heading top">
        <HeadingContainer $isFromCheckout={isFromCheckout}>
          <Title $isFromCheckout={isFromCheckout} data-testid="payments-base-title">
            {title}
            <FeatureWrapper name="Exclude netflix text">
              <>
                <br />
                <ItemTitle data-testid="payments-exclude-netflix-title">
                  {netflixOffer && t("(Excluding Netflix)")}
                </ItemTitle>
              </>
            </FeatureWrapper>
          </Title>
          <Price $isFromCheckout={isFromCheckout} data-testid="payments-base-price-text">
            {total <= 0 && offersWithPriceText ? t("{{ priceText }}", { priceText: offersWithPriceText }) : payment}
          </Price>
        </HeadingContainer>
      </FeatureWrapper>
      <ItemsList $isFromCheckout={isFromCheckout} data-testid="payments-offer-list-">
        {netflixOffer && (
          <>
            <PaymentItem
              data-testid={`payment-item-${netflixOffer.name}`}
              key={netflixOffer.packageId}
              offer={netflixOffer}
              isFromCheckout={isFromCheckout}
              isNetflixOffer
            />

            <ItemDivider />
          </>
        )}

        {offersWithoutNetflix.map((offer) => (
          <PaymentItem
            data-testid={`payment-item-${offer.name}`}
            key={offer.packageId}
            offer={offer}
            isFromCheckout={isFromCheckout}
          />
        ))}
      </ItemsList>

      <FeatureWrapper name="Show paymnetoverview heading bottom">
        <HeadingContainer $isFromCheckout={isFromCheckout}>
          <Title $isFromCheckout={isFromCheckout} data-testid="payments-total-text">
            {title}
            <FeatureWrapper name="Exclude netflix text">
              <>
                <br />
                <ItemTitle data-testid="payments-exclude-netflix-total">
                  {netflixOffer && t("(Excluding Netflix)")}
                </ItemTitle>
              </>
            </FeatureWrapper>
          </Title>
          <Price $isFromCheckout={isFromCheckout} data-testid="payments-total-price">
            {payment}
          </Price>
        </HeadingContainer>
      </FeatureWrapper>
    </PaymentsOverviewContainer>
  );
};

export default PaymentsOverview;
